import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { getCategoryName } from "@/lib/video";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";

const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "最多播放" },
  { value: "likes", label: "最多点赞" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [activeCategory, setActiveCategory] = useState("all");
  const [sort, setSort] = useState("hot");

  useEffect(() => {
    setInput(keyword);
    setActiveCategory("all");
  }, [keyword]);

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    let list = videos;

    if (q) {
      list = list.filter((v) => {
        const categoryName = getCategoryName(v.categorySlug);
        return (
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q) ||
          v.author.toLowerCase().includes(q) ||
          categoryName.includes(q)
        );
      });
    }

    if (activeCategory !== "all") {
      list = list.filter((v) => v.categorySlug === activeCategory);
    }

    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => b.publishDate.localeCompare(a.publishDate));
    else if (sort === "likes") sorted.sort((a, b) => b.likes - a.likes);
    else sorted.sort((a, b) => b.views - a.views);

    return sorted;
  }, [keyword, activeCategory, sort]);

  const hotRecommend = useMemo(
    () => [...videos].sort((a, b) => b.views - a.views).slice(0, 4),
    [],
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="px-4 py-8 md:px-6 md:py-10">
      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          搜索视频
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入关键词，快速找到你想看的短视频内容
        </p>
        <form
          onSubmit={submit}
          className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2 focus-within:border-primary-400 transition-colors duration-200"
        >
          <i className="ri-search-line text-lg text-foreground-500"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索视频标题、描述、作者或分类"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
          <button
            type="submit"
            className="shrink-0 rounded-full bg-primary-500 px-4 py-1.5 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </div>

      <div className="mt-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-foreground-600">
          {keyword ? (
            <>
              关键词
              <span className="mx-1 rounded bg-accent-100 px-2 py-0.5 font-label text-accent-900">
                {keyword}
              </span>
              共找到{" "}
              <span className="font-label text-primary-600">{results.length}</span> 个结果
            </>
          ) : (
            <>
              共 <span className="font-label text-primary-600">{results.length}</span> 个视频，可输入关键词进一步筛选
            </>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
            <button
              type="button"
              onClick={() => setActiveCategory("all")}
              className={`rounded-full px-3 py-1 text-xs font-label transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === "all"
                  ? "bg-secondary-500 text-background-50"
                  : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setActiveCategory(c.slug)}
                className={`rounded-full px-3 py-1 text-xs font-label transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === c.slug
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSort(opt.value)}
                className={`rounded-full px-3 py-1 text-xs font-label transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  sort === opt.value
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        {results.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 md:gap-4">
            {results.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <EmptyState
              title="未找到相关视频"
              description={
                keyword
                  ? `没有找到与“${keyword}”匹配的短视频，换个关键词试试，或者看看下面的热门推荐。`
                  : "还没有输入搜索关键词，输入你想看的内容试试吧。"
              }
              actions={[
                { label: "返回首页", to: "/" },
                { label: "清空关键词", onClick: () => { setInput(""); setParams({}); }, variant: "ghost" },
              ]}
            />
            <div>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-fire-line text-xl text-primary-500"></i>
                热门内容推荐
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 md:gap-4">
                {hotRecommend.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}